import { useMemo } from 'react'

import {
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  CONVERSATION_FACTS_MEASURES,
} from 'cube/constants/QueryConstants'
import { isEmpty, isNil } from 'ramda'

import {
  type ChartSeriesData,
  CubeQueryBuilder,
  useCubeQuery,
} from '@driftt/dds.reports'

import useTimePeriod from 'hooks/useTimePeriod'

export interface MedianConversationTimeQueryResult {
  metric: ChartSeriesData
  previousMetric: ChartSeriesData
  metricConversationCounts: number[]
  previousMetricConversationCounts: number[]
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
}

const resolveCubeResults = (data: any, queryName: string) => {
  if (isNil(data) || isEmpty(data.rawData())) {
    return []
  }

  return data
    .chartPivot()
    .map((item: object) => [item['x'], parseFloat(item[queryName])])
}

/***
 * Hook that uses cube to fetch the median conversation time aggregated per-day
 *
 * @returns {MedianConversationTimeQueryResult}
 */
export const useConversationMedianTimeData =
  (): MedianConversationTimeQueryResult => {
    const timePeriod = useTimePeriod()
    const { pageFilters } = useCubeReportPageFiltersContext()
    const { accessFilters } = useCubeReportAccessFiltersContext()

    const cubeQueryCurrent = new CubeQueryBuilder({})
      .withName('ConversationMedianTimeQuery')
      .withQueryName('ConversationMedianTimeQuery')
      .withFilters([...pageFilters, ...accessFilters])
      .withMeasures([
        CONVERSATION_FACTS_MEASURES.MEDIAN_DURATION_BETWEEN_FIRST_INTERACTION_AND_FIRST_CLOSE_SECONDS,
        CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT,
      ])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate(),
        'day'
      )
      .build()

    const cubeQueryPrevious = new CubeQueryBuilder({})
      .withName('ConversationMedianTimeQueryPrevious')
      .withQueryName('ConversationMedianTimeQueryPrevious')
      .withFilters([...pageFilters, ...accessFilters])
      .withMeasures([
        CONVERSATION_FACTS_MEASURES.MEDIAN_DURATION_BETWEEN_FIRST_INTERACTION_AND_FIRST_CLOSE_SECONDS,
        CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT,
      ])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.prevStart.toDate(),
        timePeriod.prevEnd.toDate(),
        'day'
      )
      .build()

    const {
      data: resultSetCurrent,
      isLoading: isLoadingCurrent,
      isError: isErrorCurrent,
    } = useCubeQuery({
      query: cubeQueryCurrent,
    })

    const { data: resultSetPrevious, isLoading: isLoadingPrevious } =
      useCubeQuery({
        query: cubeQueryPrevious,
      })

    const medianConversationLengthCurrent = useMemo(
      () => resultSetCurrent,
      [resultSetCurrent]
    )

    const medianConversationLengthPrevious = useMemo(
      () => resultSetPrevious,
      [resultSetPrevious]
    )

    const metric = useMemo(
      () =>
        resolveCubeResults(
          medianConversationLengthCurrent,
          CONVERSATION_FACTS_MEASURES.MEDIAN_DURATION_BETWEEN_FIRST_INTERACTION_AND_FIRST_CLOSE_SECONDS
        ),
      [medianConversationLengthCurrent]
    )

    const metricConversationCounts = useMemo(
      () =>
        medianConversationLengthCurrent
          ?.chartPivot()
          .map(
            (item) =>
              item[CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT] as number
          ) ?? [],
      [medianConversationLengthCurrent]
    )

    const previousMetric = useMemo(
      () =>
        resolveCubeResults(
          medianConversationLengthPrevious,
          CONVERSATION_FACTS_MEASURES.MEDIAN_DURATION_BETWEEN_FIRST_INTERACTION_AND_FIRST_CLOSE_SECONDS
        ),
      [medianConversationLengthPrevious]
    )

    const previousMetricConversationCounts = useMemo(
      () =>
        medianConversationLengthPrevious
          ?.chartPivot()
          .map(
            (item) =>
              item[CONVERSATION_FACTS_MEASURES.CONVERSATION_COUNT] as number
          ) ?? [],
      [medianConversationLengthPrevious]
    )

    const isLoading = useMemo(
      () => isLoadingCurrent || isLoadingPrevious,
      [isLoadingCurrent, isLoadingPrevious]
    )

    const hasNoData = useMemo(
      () => !isLoading && isNil(medianConversationLengthCurrent),
      [isLoading, medianConversationLengthCurrent]
    )

    const hasError = useMemo(
      () => !isLoading && isErrorCurrent,
      [isLoading, isErrorCurrent]
    )

    return useMemo(
      () => ({
        metric,
        previousMetric,
        isLoading,
        hasNoData,
        hasError,
        metricConversationCounts,
        previousMetricConversationCounts,
      }),
      [
        metric,
        previousMetric,
        isLoading,
        hasNoData,
        hasError,
        metricConversationCounts,
        previousMetricConversationCounts,
      ]
    )
  }

export default useConversationMedianTimeData
